import type { AnyComment, Comment } from '../model/types';

/**
 * A new comment as the thread shows it before the request lands: under its
 * parent when it is a reply to one, otherwise at the end as a top-level
 * comment with no replies yet.
 */
export function insertOptimisticComment(
  comments: Comment[],
  optimistic: AnyComment,
  { replyToId, parentId }: { replyToId?: number; parentId?: number | null }
): Comment[] {
  if (replyToId && parentId) {
    return comments.map((comment) =>
      comment.id === parentId
        ? { ...comment, replies: [...(comment.replies || []), optimistic] }
        : comment
    );
  }

  return [...comments, { ...optimistic, replies: [] } as Comment];
}

/**
 * A like or unlike as the thread shows it before the request lands.
 *
 * Known divergence, kept for parity: only top-level comments are matched, so
 * liking a reply changes nothing until the thread refreshes, and the count is
 * moved by one either way without a floor.
 */
export function toggleCommentLike(comments: Comment[], commentId: number): Comment[] {
  return comments.map((comment) =>
    comment.id === commentId
      ? {
          ...comment,
          liked: !comment.liked,
          likes: comment.liked ? comment.likes - 1 : comment.likes + 1,
        }
      : comment
  );
}
